import { useTranslation } from "react-i18next";
import { ChevronDown } from "@digital-twins/design-system/icons";
import {
  CREATIVE_FORMATS,
  CREATIVE_FORMAT_LIMITS,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Text } from "@/components/ui/Text";
import type { ThreadImageFormatPickerProps } from "@/types/components/thread-image-approval";
import { canAddImageFormat, orderedImageFormats } from "./imageApprovalGroups";
import { imageFormatLabel, imageFormatsSummary } from "@/utils/imageFormatCopy";

/** The card's Format dropdown: one image per ticked format, inside the caps. */
export function ThreadImageFormatPicker(props: ThreadImageFormatPickerProps) {
  const { selected, rows, disabled, onChange } = props;

  const { t } = useTranslation("app");

  const canAdd = canAddImageFormat(rows, selected.length);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full min-w-0 justify-between gap-2 sm:w-auto"
          disabled={disabled}
          data-testid="image-approval-format-trigger"
        >
          <span className="truncate">{imageFormatsSummary(t, selected)}</span>
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {CREATIVE_FORMATS.map((format) => {
          const isChecked = selected.includes(format.id);
          const isLastTicked = isChecked && selected.length === 1;
          return (
            <DropdownMenuCheckboxItem
              key={format.id}
              checked={isChecked}
              disabled={isLastTicked || (!isChecked && !canAdd)}
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(next) => {
                onChange(
                  orderedImageFormats(
                    next
                      ? [...selected, format.id]
                      : selected.filter((id) => id !== format.id),
                  ),
                );
              }}
              data-testid={`image-approval-format-option-${format.id}`}
            >
              <span className="flex flex-col">
                <span>{imageFormatLabel(t, format.id)}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`threadImage.approval.formats.${format.id}.platforms`)}
                </span>
              </span>
            </DropdownMenuCheckboxItem>
          );
        })}
        {/* Same height with or without the line: its appearing must not flip the open menu. */}
        {canAdd && (
          <div className="invisible" aria-hidden data-testid="image-approval-format-cap-space">
            <DropdownMenuSeparator />
            <Text size="xs" color="muted" className="px-2 py-1.5">
              {t("threadImage.approval.formatCap", {
                limit: CREATIVE_FORMAT_LIMITS.imagesPerRequest,
              })}
            </Text>
          </div>
        )}
        {!canAdd && (
          <>
            <DropdownMenuSeparator />
            <Text
              size="xs"
              color="muted"
              className="px-2 py-1.5"
              data-testid="image-approval-format-cap"
            >
              {t("threadImage.approval.formatCap", {
                limit: CREATIVE_FORMAT_LIMITS.imagesPerRequest,
              })}
            </Text>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
